import type { AggFunc, ColumnDef, EntityDef } from "../entities/types";
import {
  type AgGridColumnFilter,
  translateAgGridFilterModel,
} from "./ag-grid-filters";
import { measureAlias } from "./measure-alias";
import type { Filter, Sort, StructuredQueryConfig } from "./types";

export type GridRowGroupCol = {
  field?: string | null;
  id?: string | null;
};

export type GridValueCol = {
  field?: string | null;
  id?: string | null;
  aggFunc?: string | null;
};

export type GridSortModel = {
  colId: string;
  sort: "asc" | "desc";
};

export type GridEntityDef = Pick<EntityDef, "name" | "columns">;

export class ExploreGridRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ExploreGridRequestError";
  }
}

export type BuildStructuredQueryInput = {
  entity: GridEntityDef;
  rowGroupCols: GridRowGroupCol[];
  groupKeys: string[];
  valueCols: GridValueCol[];
  pivotCols?: GridRowGroupCol[];
  pivotMode?: boolean;
  sortModel: GridSortModel[];
  filterModel?: Record<string, AgGridColumnFilter | null | undefined> | null;
  /** When set, used instead of translating filterModel. */
  filters?: Filter[];
  startRow?: number | null;
  endRow?: number | null;
  dateRange?: StructuredQueryConfig["dateRange"];
};

export function buildStructuredQueryFromGridRequest(
  input: BuildStructuredQueryInput,
): StructuredQueryConfig {
  if (
    input.pivotMode ||
    (input.pivotCols != null && input.pivotCols.length > 0)
  ) {
    throw new ExploreGridRequestError(
      "Pivot is not supported. Use Summary mode for charts and grouped data.",
    );
  }

  const columnMap = new Map(input.entity.columns.map((c) => [c.field, c]));
  const visibleFields = input.entity.columns
    .filter((c) => !c.phi)
    .map((c) => c.field);

  const req = input;
  const atLeafLevel =
    req.rowGroupCols.length > 0 &&
    req.groupKeys.length >= req.rowGroupCols.length;

  const parentFilters = buildParentGroupFilters(
    req.rowGroupCols,
    req.groupKeys,
  );
  const gridFilters =
    req.filters ?? translateAgGridFilterModel(req.filterModel, columnMap);

  let groupBy: StructuredQueryConfig["groupBy"] = [];
  let measures: StructuredQueryConfig["measures"] = [];
  let columns: string[] = [];

  if (atLeafLevel) {
    columns = visibleFields;
  } else if (req.rowGroupCols.length > 0) {
    groupBy = req.rowGroupCols.slice(0, req.groupKeys.length + 1).map((col) => {
      const field = col.field ?? col.id ?? "";
      return { field };
    });

    measures = req.valueCols.map((col) => ({
      field: col.field ?? col.id ?? "*",
      func: normalizeAggFunc(col.aggFunc),
      alias: col.field ?? col.id ?? undefined,
    }));

    if (measures.length === 0) {
      measures = [{ field: "*", func: "count", alias: "ag_count" }];
    }
  } else {
    columns = visibleFields;
  }

  const sort = resolveGridSortModel(
    req.sortModel,
    groupBy,
    measures,
    columnMap,
    columns,
  );

  return {
    kind: "structured",
    entity: input.entity.name,
    columns,
    groupBy,
    measures,
    filters: [...parentFilters, ...gridFilters],
    dateRange: input.dateRange,
    sort,
    limit: (req.endRow ?? 100) - (req.startRow ?? 0),
    offset: req.startRow ?? 0,
  };
}

function buildParentGroupFilters(
  rowGroupCols: GridRowGroupCol[],
  groupKeys: string[],
): Filter[] {
  const filters: Filter[] = [];

  for (let i = 0; i < groupKeys.length; i++) {
    const key = groupKeys[i];
    const field = rowGroupCols[i]?.field ?? rowGroupCols[i]?.id ?? "";
    filters.push({ field, op: "eq", value: key });
  }

  return filters;
}

function normalizeAggFunc(aggFunc: string | null | undefined): AggFunc {
  if (aggFunc === "count_distinct" || aggFunc === "countDistinct") {
    return "count_distinct";
  }
  if (
    aggFunc === "count" ||
    aggFunc === "sum" ||
    aggFunc === "avg" ||
    aggFunc === "min" ||
    aggFunc === "max" ||
    aggFunc === "median" ||
    aggFunc === "p95"
  ) {
    return aggFunc;
  }
  return "count";
}

export function defaultExploreDateFilterModel(
  defaultDateField: string,
  days = 30,
): Record<string, AgGridColumnFilter> {
  const until = new Date();
  const since = new Date();
  since.setDate(since.getDate() - days);

  return {
    [defaultDateField]: {
      filterType: "date",
      type: "inRange",
      dateFrom: formatAgGridDate(since),
      dateTo: formatAgGridDate(until),
    },
  };
}

/** Default Explore filters: date window plus active (non-cancelled) results only. */
export function defaultExploreFilterModel(
  defaultDateField: string,
  days = 30,
): Record<string, AgGridColumnFilter> {
  return {
    ...defaultExploreDateFilterModel(defaultDateField, days),
    is_cancelled: {
      filterType: "set",
      values: [false],
    },
  };
}

/** UTC midnight so timestamptz casts are timezone-independent. */
function formatAgGridDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T00:00:00.000Z`;
}

export function resolveGridSortModel(
  sortModel: GridSortModel[],
  groupBy: StructuredQueryConfig["groupBy"],
  measures: StructuredQueryConfig["measures"],
  columnMap: Map<string, ColumnDef>,
  flatColumns: string[],
): Sort[] {
  return sortModel
    .map((sort) =>
      resolveGridSort(sort, groupBy, measures, columnMap, flatColumns),
    )
    .filter((sort): sort is Sort => sort !== null);
}

function resolveGridSort(
  sort: GridSortModel,
  groupBy: StructuredQueryConfig["groupBy"],
  measures: StructuredQueryConfig["measures"],
  columnMap: Map<string, ColumnDef>,
  flatColumns: string[],
): Sort | null {
  let field = sort.colId;

  // AG Grid row-group UI column — unqualified id breaks Postgres (`ag` - `Grid` - …).
  // Unqualified "ag-Grid-AutoColumn" (no suffix) is the expanding group column for the
  // deepest active level; map it to that level's field via groupBy[groupBy.length - 1].
  if (
    field === "ag-Grid-AutoColumn" ||
    field.startsWith("ag-Grid-AutoColumn-")
  ) {
    const suffix = field.slice("ag-Grid-AutoColumn-".length);
    field = suffix || groupBy[groupBy.length - 1]?.field || "";
  }

  if (!field || field.startsWith("ag-")) {
    return null;
  }

  if (groupBy.length > 0) {
    if (groupBy.some((group) => group.field === field)) {
      return { field, dir: sort.sort };
    }
    if (measures.some((measure) => measureAlias(measure) === field)) {
      return { field, dir: sort.sort };
    }
    return null;
  }

  if (!columnMap.has(field)) {
    return null;
  }

  if (flatColumns.length > 0 && !flatColumns.includes(field)) {
    return null;
  }

  return { field, dir: sort.sort };
}
